import { z } from 'zod'

const eventSchema = z.object({
  method: z.string({ message: 'Event method is required' }),
  url: z.string({ message: 'Event URL is required' }),
  isUpdated: z.boolean(),
})

const musicSchema = z.object({
  file: z.string({ message: 'Music file is required' }),
  prompt_type: z.string({ message: 'Music prompt type is required' }),
  url: z.string({ message: 'Music URL is required' }),
})

const chatConfigSchema = z.object({
  conversation_closed_message: z.string({ message: 'Conversation closed message is required' }),
  post_agent_assignment_message: z.string({ message: 'Post agent assignment message is required' }),
  pre_agent_assignment_message: z.string({ message: 'Pre agent assignment message is required' }),
  queue_chat_timeout: z.number({ message: 'Queue chat timeout is required' }),
})

const whatsappConfigSchema = z.object({
  conversation_closed_message: z.string({ message: 'Conversation closed message is required' }),
  post_agent_assignment_message: z.string({ message: 'Post agent assignment message is required' }),
  pre_agent_assignment_message: z.string({ message: 'Pre agent assignment message is required' }),
  queue_whatsapp_timeout: z.number({ message: 'Queue whatsapp timeout is required' }),
})

const messageConfigSchema = z.object({
  conversation_closed_message: z.string({ message: 'Conversation closed message is required' }),
  post_agent_assignment_message: z.string({ message: 'Post agent assignment message is required' }),
  pre_agent_assignment_message: z.string({ message: 'Pre agent assignment message is required' }),
  queue_message_timeout: z.number({ message: 'Queue message timeout is required' }),
})

const callbackConfigSchema = z.object({
  queue_callback_timeout: z.number({ message: 'Queue callback timeout is required' }),
})

const taskConfigSchema = z.object({
  queue_task_timeout: z.number({ message: 'Queue task timeout is required' }),
})

const autoAcceptCallSchema = z.object({
  allow_auto_accept: z.boolean(),
  auto_accept_delay: z.number({ message: 'Auto accept delay is required' }),
})
export const queueSchema = z.object({
  queue_priority: z
    .number({ message: 'Queue priority is required' })
    .min(1, 'Queue priority is required'),
  dequeue_order: z.string(),
  queue_capacity: z
    .number({ message: 'Queue capacity is required' })
    .min(50, 'Queue capacity must be at least 50')
    .max(100, 'Queue capacity must be at most 100'),
  events: z.object({
    after_conversation_work_completed: eventSchema,
    agent_assigned: eventSchema,
    conversation_transferred: eventSchema,
  }),
  queue: z.string().min(1, 'Queue name is required'),
  channel: z.array(z.string()),
  queue_call_timeout: z.number({ message: 'Queue call timeout is required' }),
  customer_hold_music: musicSchema,
  agent_hold_music: musicSchema,
  queue_call_allowed_inputs: z.array(z.string()).optional(),
  chat_config: chatConfigSchema,
  whatsapp_config: whatsappConfigSchema,
  message_config: messageConfigSchema,
  callback_config: callbackConfigSchema,
  task_config: taskConfigSchema,
})
export const HANDOFF_CONTEXT_SOURCES = ['external'] as const
export const HANDOFF_CONTEXT_FIELDS = [
  'original_caller',
  'summary',
  'transfer_reason',
  'collected_fields',
  'transcript',
] as const

// Display policy only; the backend enforces sources/fields and binds calls from its own voice receipt.
export const handoffContextPolicySchema = z.object({
  enabled: z.boolean(),
  sources: z.array(z.enum(HANDOFF_CONTEXT_SOURCES)),
  source_uuid: z.string().optional(),
  fields: z.array(z.enum(HANDOFF_CONTEXT_FIELDS)),
})

export const handoffContextSchema = handoffContextPolicySchema.superRefine((ctx, issue) => {
  if (ctx.enabled && !ctx.source_uuid) {
    issue.addIssue({
      code: 'custom',
      path: ['source_uuid'],
      message: 'Choose the external source',
    })
  }
})

export const qnrSchema = z.object({
  selectSource: z.string().optional(),
  context: handoffContextSchema.optional(),
  routing_teams: z.array(z.any()),
  routing_agents: z.array(z.string({ message: 'Routing agents are required' })),
  routing_variables: z.array(z.string()).optional(),
  routing_skills_filter: z.array(z.any()),
  routing: z.string({ message: 'Routing strategy is required' }),
  agent_limit: z
    .number({ message: 'Agent limit is required' })
    .min(1, 'Agent limit must be at least 1')
    .max(10, 'Agent limit must be at most 10'),
  retry_limit: z
    .number({ message: 'Retry limit is required' })
    .max(3, 'Retry limit must be at most 3')
    .min(0, 'Retry limit must be at least 0'),
  sticky_routing_options: z.string({ message: 'Sticky routing options are required' }),
  expiry_time_value: z.number({ message: 'Expiry time value is required' }),
  expiry_time_unit: z.string({ message: 'Expiry time unit is required' }),
  expiry: z.boolean(),
  auto_accept_call: autoAcceptCallSchema,
  queue: z.string({ message: 'Queue is required' }),
  queue_call_allowed_inputs: z.array(z.string()).optional(),
})
